import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Hub } from 'aws-amplify/utils'
import { getDefaultLocationBeforeLogin } from '../LoginUtills'
import { logger } from 'services/logger'
import { isClient } from 'utils/runtimeUtils'
import { mixtilesAxios } from '../../utils/ApiUtils'
import {
  confirmEmailCode as cognitoConfirmEmailCode,
  getAccessToken,
  getIdToken,
  getUser,
  initCognito,
  initiateEmailSignIn as cognitoInitiateEmailSignIn,
  signInWithApple as cognitoSignInWithApple,
  signInWithGoogle as cognitoSignInWithGoogle,
  signOut as cognitoSignOut,
} from './cognito'
import useOnMount from '../../hooks/useOnMount'
import { type CognitoUser } from './cognito.types'
import { useKeys } from '../KeysProvider'

interface AuthContextType {
  isAuthenticated: boolean
  user: CognitoUser | undefined // Use the simplified CognitoUser type
  isLoading: boolean
  initiateEmailSignIn: (email: string) => Promise<any> // Returns Cognito challenge data
  confirmEmailCode: (userChallenge: any, code: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithApple: () => Promise<void>
  getAccessToken: () => Promise<string | null>
  getIdToken: () => Promise<string | null>
  logout: (returnToUrl?: string) => Promise<void>
  error: any | null
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: undefined,
  isLoading: true,
  initiateEmailSignIn: async () => {
    throw new Error('AuthProvider not initialized')
  },
  confirmEmailCode: async () => {
    throw new Error('AuthProvider not initialized')
  },
  signInWithGoogle: async () => {
    throw new Error('AuthProvider not initialized')
  },
  signInWithApple: async () => {
    throw new Error('AuthProvider not initialized')
  },
  getAccessToken: async () => null,
  getIdToken: async () => null,
  logout: async () => {},
  error: null,
})

function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthContextProvider')
  }

  return context
}

function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<CognitoUser | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<any | null>(null)
  const keys = useKeys()

  useOnMount(() => initCognito(keys))

  const checkUser = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const cognitoUser: CognitoUser = await getUser()
      setUser(cognitoUser)
    } catch (err) {
      setUser(undefined)
      if (
        err instanceof Error &&
        !err.message.includes('User needs to be authenticated to call this API')
      ) {
        logger.error('Error fetching user on load', err)
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    checkUser()
  }, [checkUser])

  useEffect(() => {
    const listener = (data: any) => {
      logger.info('user event', data.payload.event)
      switch (data.payload.event) {
        case 'signInWithRedirect':
          setIsLoading(true)
          break
        case 'signInWithRedirect_failure':
          setError(data.payload.data)
          setIsLoading(false)
          break
        case 'signedIn':
        case 'tokenRefresh':
          checkUser()
          break
        case 'signedOut':
          // since we reload after sign out, we don't need to set user to undefined here
          // this actually causes a bug in TilesProvider, since api.isAuthenticated changes
          // causing an effect to be triggered and:  saveTiles(tiles, STORAGE_KEYS[tilesSource], forcePhotoBank)
          // is called. So I removed this line (which did not exists in auth0 flow).
          // setUser(undefined)
          setIsLoading(false)
          break
        default:
          break
      }
    }

    const unsubscribe = Hub.listen('auth', listener)
    return () => unsubscribe()
  }, [checkUser])

  const initiateEmailSignIn = useCallback(async (email: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await cognitoInitiateEmailSignIn(email, () =>
        mixtilesAxios.post('v1/auth/createUserCognito', { email })
      )
      setIsLoading(false)
      return result
    } catch (err) {
      logger.error('Email sign in initiation failed', err)
      setError(err)
      setIsLoading(false)
      throw err
    }
  }, [])

  const confirmEmailCode = useCallback(
    async (userChallenge: any, code: string) => {
      setIsLoading(true)
      setError(null)
      try {
        await cognitoConfirmEmailCode(userChallenge, code)
      } catch (err) {
        logger.error('Email code confirmation failed', err)
        setError(err)
        setIsLoading(false)
        throw err
      }
    },
    []
  )

  const signInWithGoogle = useCallback(async () => {
    setError(null)
    try {
      await cognitoSignInWithGoogle()
    } catch (err) {
      logger.error('Google sign in failed', err)
      setError(err)
    }
  }, [])

  const signInWithApple = useCallback(async () => {
    setError(null)
    try {
      await cognitoSignInWithApple()
    } catch (err) {
      logger.error('Apple sign in failed', err)
      setError(err)
    }
  }, [])

  const logout = useCallback(async (returnToUrl?: string) => {
    setIsLoading(true)
    setError(null)
    try {
      await cognitoSignOut()
      if (isClient() && returnToUrl) {
        window.location.href = returnToUrl
      } else if (isClient()) {
        const pathname = getDefaultLocationBeforeLogin()
        window.location.href = window.location.origin + pathname
      }
    } catch (err) {
      logger.error('Sign out failed', err)
      setError(err)
      setIsLoading(false)
    }
  }, [])

  const contextValue = useMemo(
    () => ({
      isAuthenticated: !!user,
      user,
      isLoading,
      initiateEmailSignIn,
      confirmEmailCode,
      signInWithGoogle,
      signInWithApple,
      getAccessToken,
      getIdToken,
      logout,
      error,
    }),
    [
      user,
      isLoading,
      error,
      initiateEmailSignIn,
      confirmEmailCode,
      signInWithGoogle,
      signInWithApple,
      getAccessToken,
      getIdToken,
      logout,
    ]
  )

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export { AuthProvider, useAuth }
